import React, {useState, useEffect, useRef, useCallback} from 'react'
import Button from 'app/components/Button'
import Stepper from '../../components/Stepper'
import {CloudPageTitle} from '_metronic/layout/core/CloudPageData'
import Input from 'app/components/Input'
import cloudService from '../../services/cloud.services'
import {useAuth} from 'app/modules/auth'
import {useNavigate} from 'react-router-dom'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {addDoc, collection, doc, getDocs, query, serverTimestamp, where} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {ToastContainer} from 'react-toastify'

const CloudConfiguration = () => {
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const [projectId, setProjectId] = useState('')
  const [cloudName, setCloudName] = useState('')
  const [cloudPurpose, setCloudPurpose] = useState('')
  const [currentStep, setCurrentStep] = useState(1)
  const [isSaving, setIsSaving] = useState(false)
  const [uploadedJsonData, setUploadedJsonData] = useState<any>(null)
  const [cloudId, setCloudId] = useState<string | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const cloudIdRef = useRef<string | null>(null)
  const currentStepRef = useRef<number>(1)
  const [steps, setSteps] = useState([
    {
      id: 1,
      title: 'Sign in and connect project ID',
      isCompleted: false,
      isActive: true,
    },
    {
      id: 2,
      title: 'Enable Cloud APIs',
      isCompleted: false,
      isActive: false,
    },
    {
      id: 3,
      title: 'Create service account',
      isCompleted: false,
      isActive: false,
    },
    {
      id: 4,
      title: 'Create access key',
      isCompleted: false,
      isActive: false,
    },
    {
      id: 5,
      title: 'Name your cloud connection',
      isCompleted: false,
      isActive: false,
    },
  ])

  const deleteIncompleteCloud = useCallback(
    async (cloudIdToDelete = cloudId) => {
      if (!currentUser?.tenantId || !cloudIdToDelete) {
        return
      }

      try {
        await cloudService.deleteCloud(currentUser.tenantId, cloudIdToDelete)
        if (cloudIdToDelete === cloudId) {
          setCloudId(null)
          setUploadedJsonData(null)
        }
      } catch (error) {
        console.error('Error deleting incomplete cloud:', error)
      }
    },
    [currentUser?.tenantId, cloudId]
  )

  useEffect(() => {
    cloudIdRef.current = cloudId
  }, [cloudId])

  useEffect(() => {
    currentStepRef.current = currentStep
  }, [currentStep])

  useEffect(() => {
    return () => {
      if (cloudIdRef.current && currentStepRef.current < 5) {
        deleteIncompleteCloud(cloudIdRef.current)
      }
    }
  }, [deleteIncompleteCloud])

  const handleContinue = async () => {
    setIsSaving(true)
    try {
      if (currentStep === 1 && projectId.trim()) {
        if (!cloudId) {
          const newCloudId = await createInitialCloud()
          if (newCloudId) {
            setCloudId(newCloudId)
          }
        } else {
          await updateCloudProjectId()
        }

        const updatedSteps = steps.map((step) => ({
          ...step,
          isCompleted: step.id === currentStep ? true : step.isCompleted,
          isActive: step.id === currentStep + 1 ? true : false,
        }))

        setSteps(updatedSteps)
        setCurrentStep((prev) => prev + 1)
      } else if (currentStep === 2 || currentStep === 3) {
        await updateCloudStep(currentStep)

        const updatedSteps = steps.map((step) => ({
          ...step,
          isCompleted: step.id === currentStep ? true : step.isCompleted,
          isActive: step.id === currentStep + 1 ? true : false,
        }))

        setSteps(updatedSteps)
        setCurrentStep((prev) => prev + 1)
      } else if (currentStep === 4) {
        await updateCloudStep(currentStep)

        const updatedSteps = steps.map((step) => ({
          ...step,
          isCompleted: step.id === currentStep ? true : step.isCompleted,
          isActive: step.id === currentStep + 1 ? true : false,
        }))

        setSteps(updatedSteps)
        setCurrentStep((prev) => prev + 1)
      } else if (currentStep === 5) {
        await completeCloudConfiguration()

        const updatedSteps = steps.map((step) => ({
          ...step,
          isCompleted: step.id === currentStep ? true : step.isCompleted,
          isActive: false,
        }))

        setSteps(updatedSteps)
        setTimeout(() => {
          navigate('/vulnerabilities/clouds')
        }, 1000)
      }
    } catch (error) {
      showErrorToast({message: 'Failed to save step. Please try again.'})
    } finally {
      setIsSaving(false)
    }
  }

  const handleGoBack = () => {
    if (currentStep > 1) {
      const updatedSteps = steps.map((step) => ({
        ...step,
        isCompleted: step.id === currentStep ? false : step.isCompleted,
        isActive: step.id === currentStep - 1 ? true : false,
      }))

      setSteps(updatedSteps)
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (file && file.type === 'application/json') {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const result = e.target?.result
          if (typeof result === 'string') {
            const jsonData = JSON.parse(result)
            setUploadedJsonData(jsonData)
          } else {
            throw new Error('Invalid file content')
          }
        } catch (error) {
          showErrorToast({message: 'Invalid JSON file. Please select a valid JSON file.'})
        }
      }
      reader.readAsText(file)
      showSuccessToast({message: 'JSON file uploaded successfully'})
    } else {
      showErrorToast({message: 'Please select a valid JSON file.'})
    }
  }

  const createInitialCloud = async () => {
    if (!currentUser?.tenantId) {
      return null
    }

    try {
      const cloudData = {
        name: `GCP-${projectId}`,
        provider: 'GCP',
        account_id: projectId,
        status: 'configuring',
        configurationSteps: {
          step1: {projectId, completed: true},
          step2: {apisEnabled: false, completed: false},
          step3: {serviceAccountCreated: false, completed: false},
          step4: {accessKeyCreated: false, completed: false},
          step5: {cloudNamed: false, completed: false},
        },
        last_scanned: null,
        ignored: '0',
      }

      const newCloudId = await cloudService.addCloud(currentUser.tenantId, cloudData)

      if (newCloudId) {
        return newCloudId
      } else {
        throw new Error('Failed to create initial cloud')
      }
    } catch (error) {
      showErrorToast({message: 'Error creating initial cloud:'})
      throw error
    }
  }

  const updateCloudProjectId = async () => {
    if (!currentUser?.tenantId || !cloudId) {
      showErrorToast({message: 'User not authenticated or cloud ID missing'})
      return
    }

    try {
      const updateData = {
        name: `GCP-${projectId}`,
        account_id: projectId,
        'configurationSteps.step1': {projectId, completed: true},
      }

      await cloudService.updateCloud(currentUser.tenantId, cloudId, updateData)
    } catch (error) {
      showErrorToast({message: 'Error updating project ID:'})
      throw error
    }
  }

  const updateCloudStep = async (stepNumber) => {
    if (!currentUser?.tenantId) {
      showErrorToast({message: 'User not authenticated'})
      return
    }

    if (!cloudId) {
      showErrorToast({message: 'Cloud configuration not found. Please start from step 1.'})
      return
    }

    try {
      let updateData = {}

      switch (stepNumber) {
        case 2:
          updateData = {
            'configurationSteps.step2': {apisEnabled: true, completed: true},
          }
          break
        case 3:
          updateData = {
            'configurationSteps.step3': {serviceAccountCreated: true, completed: true},
          }
          break
        case 4:
          updateData = {
            'configurationSteps.step4': {
              accessKeyCreated: uploadedJsonData ? true : false,
              completed: uploadedJsonData ? true : false,
            },
            serviceAccountData: uploadedJsonData,
          }
          if (uploadedJsonData) {
            await cloudService.uploadJsonToCloud(
              currentUser.tenantId,
              cloudId,
              uploadedJsonData,
              'json'
            )
          }
          break
        default:
          break
      }

      if (Object.keys(updateData).length > 0) {
        await cloudService.updateCloud(currentUser.tenantId, cloudId, updateData)
      }
    } catch (error) {
      showErrorToast({message: `Error updating step ${stepNumber}:`})
      throw error
    }
  }

  const completeCloudConfiguration = async () => {
    if (!currentUser?.tenantId || !cloudId) {
      showErrorToast({message: 'User not authenticated or cloud ID missing'})
      return
    }

    try {
      const updateData = {
        name: cloudName,
        purpose: cloudPurpose,
        status: 'configured',
        'configurationSteps.step5': {cloudNamed: true, completed: true},
        configuredAt: new Date().toISOString(),
      }

      await cloudService.updateCloud(currentUser.tenantId, cloudId, updateData)

      if (uploadedJsonData) {
        setIsScanning(true)
        try {
          showSuccessToast({message: 'Starting security scan...'})
          
          const serviceAccountData = {
            client_email: uploadedJsonData.client_email,
            private_key: uploadedJsonData.private_key,
            project_id: uploadedJsonData.project_id,
            private_key_id: uploadedJsonData.private_key_id,
          }

          const scanResults = await cloudService.runCloudSploitScan(
            serviceAccountData,
            uploadedJsonData.project_id
          )

          if (scanResults && scanResults.success) {
            await cloudService.updateCloudWithScanResults(
              currentUser.tenantId,
              cloudId,
              scanResults,
              uploadedJsonData.project_id
            )

            await createIntegrationAndVendorRecords()
            showSuccessToast({
              message: 'Cloud configuration and security scan completed successfully!',
            })
          } else {
            showErrorToast({
              message: `Security scan failed: ${scanResults?.error || 'No results returned'}`,
            })
            return
          }
        } catch (scanError: any) {
          let errorMessage = 'Unknown error'
          if (scanError.message) {
            if (scanError.message.includes('Credential validation failed:')) {
              errorMessage = scanError.message.replace('Credential validation failed: ', '')
            } else if (scanError.message.includes('500')) {
              errorMessage = 'Cloud function server error. Please try again later.'
            } else {
              errorMessage = scanError.message
            }
          }

          showErrorToast({message: `Security scan failed: ${errorMessage}`})
          return
        } finally {
          setIsScanning(false)
        }
      } else {
        await createIntegrationAndVendorRecords()
        showSuccessToast({message: 'Cloud configuration completed successfully!'})
      }
    } catch (error) {
      showErrorToast({message: 'Error completing cloud configuration:'})
      throw error
    }
  }

  const createIntegrationAndVendorRecords = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const integrationsRef = collection(tenantRef, 'integrations')
      const vendorsRef = collection(tenantRef, 'vendors')
      const activitiesRef = collection(tenantRef, 'activities')

      const currentDate = new Date()
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`

      const existingIntegrationQuery = query(
        integrationsRef,
        where('name', '==', 'Google Cloud Platform'),
        where('type', '==', 'cloud_provider')
      )
      const existingIntegrationSnapshot = await getDocs(existingIntegrationQuery)

      if (existingIntegrationSnapshot.empty) {
        const integrationData = {
          name: 'Google Cloud Platform',
          group: 'Cloud Provider',
          img_path: '/integrations/gcp.svg',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          type: 'cloud_provider',
          cloudId: cloudId,
          projectId: projectId,
        }

        await addDoc(integrationsRef, integrationData)
      }

      const existingVendorQuery = query(
        vendorsRef,
        where('vendor_name', '==', 'Google Cloud Platform')
      )
      const existingVendorSnapshot = await getDocs(existingVendorQuery)

      if (existingVendorSnapshot.empty) {
        const vendorData = {
          authentication_type: 'Service Account',
          category: 'Cloud Provider',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [
            {
              name: 'gcp_logo.png',
              size: 3395,
              url: '/integrations/gcp.svg',
            },
          ],
          owner: currentUser?.userName || '',
          policy_url: 'https://cloud.google.com/security',
          reliance: '',
          risk_level: '',
          tenantId: currentUser?.tenantId,
          terms_url: 'https://cloud.google.com/terms',
          two_factor: true,
          uid: currentUser?.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'Google Cloud Platform',
          website_url: 'https://cloud.google.com/',
        }

        await addDoc(vendorsRef, vendorData)
      }

      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} completed Google Cloud Platform configuration, ran vulnerability scan, and connected integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Cloud configuration and vulnerability scan completed',
        target: 'Cloud Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
    } catch (error) {
      console.error('Error creating integration and vendor records:', error)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <h3 className='fw-bold fs-1 text-gray-900 mb-2'>
              Google Cloud Platform (GCP) Configuration
            </h3>

            <p className='text-gray-900 fs-6 mb-4'>Let's start with getting your project ID</p>

            <div className='mb-4'>
              <h6 className='fw-semibold mb-3'>To get your project ID:</h6>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Log into your{' '}
                    <a
                      href='https://console.cloud.google.com/'
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary text-decoration-none fw-semibold d-inline-flex align-items-center'
                    >
                      GCP Account{' '}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-external-link ms-1'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                        <path d='M11 13l9 -9' />
                        <path d='M15 4h5v5' />
                      </svg>
                    </a>
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>Open the project overview modal from the top left</span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>Locate your project ID</span>
                </li>
              </ul>
            </div>

            <div className='mb-4'>
              <p className='text-gray-700 mb-2'>
                Please paste the ID for the project you would like to connect into the field below
              </p>
              <Input
                type='text'
                className='form-control form-control-lg'
                placeholder='Compliance-270418'
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                style={{borderRadius: '8px'}}
              />
            </div>
          </>
        )

      case 2:
        return (
          <>
            <h3 className='fw-bold fs-1 text-gray-900 mb-2'>Enable Cloud APIs</h3>

            <p className='text-gray-900 fs-6 mb-4'>
              We first need to enable some cloud API's which allows us read-only capabilities to
              inspect the policies of your cloud resources.
            </p>

            <div className='mb-4'>
              <h6 className='fw-semibold mb-3'>To start:</h6>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Click the <strong>"Activate Cloud Shell"</strong> icon in the top right in the
                    GCP console.
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    You will see that at the bottom of your screen, an interactive shell was started
                    where you can enter commands.
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>The following command will enable API access to the resources in GCP:</span>
                </li>
              </ul>
            </div>

            <div className='mb-4'>
              <div
                className='bg-light p-3 rounded position-relative'
                style={{fontFamily: 'monospace', fontSize: '14px'}}
              >
                <button
                  className='btn btn-icon btn-light position-absolute top-0 end-0 m-2'
                  style={{zIndex: 10}}
                  onClick={(event) => {
                    const commandText =
                      'gcloud services enable compute.googleapis.com storage-component.googleapis.com iam.googleapis.com container.googleapis.com logging.googleapis.com monitoring.googleapis.com cloudresourcemanager.googleapis.com cloudkms.googleapis.com bigquery-json.googleapis.com dns.googleapis.com sqladmin.googleapis.com cloudasset.googleapis.com deploymentmanager.googleapis.com dataproc.googleapis.com artifactregistry.googleapis.com composer.googleapis.com'
                    navigator.clipboard.writeText(commandText).then(() => {
                      showSuccessToast({message: 'Command copied to clipboard!'})
                      const btn = event.target as HTMLButtonElement
                      const originalText = btn.innerHTML
                      btn.innerHTML = '✓'
                      btn.classList.add('btn-success')
                      btn.classList.remove('btn-light')
                      setTimeout(() => {
                        btn.innerHTML = originalText
                        btn.classList.remove('btn-success')
                        btn.classList.add('btn-light')
                      }, 2000)
                    })
                  }}
                  title='Copy command'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-copy'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z' />
                    <path d='M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1' />
                  </svg>
                </button>
                gcloud services enable compute.googleapis.com storage-component.googleapis.com
                iam.googleapis.com container.googleapis.com logging.googleapis.com
                monitoring.googleapis.com cloudresourcemanager.googleapis.com
                cloudkms.googleapis.com bigquery-json.googleapis.com dns.googleapis.com
                sqladmin.googleapis.com cloudasset.googleapis.com deploymentmanager.googleapis.com
                dataproc.googleapis.com artifactregistry.googleapis.com composer.googleapis.com
              </div>
              <p className='text-gray-700 mt-2 fs-7'>
                Copy and paste it in the interactive shell and enable it. This should return in a
                success message being shown.
              </p>
            </div>
          </>
        )

      case 3:
        return (
          <>
            <h3 className='fw-bold fs-1 text-gray-900 mb-2'>Create service account</h3>

            <p className='text-gray-900 fs-6 mb-4'>
              We need to create a service account and assign some permissions.
            </p>

            <div className='mb-4'>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    The below button will take you to the service account creation page in GCP.
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Give the account a name, you can use whatever you'd like, but we recommend
                    including <strong>'SecureSlate'</strong> in there somewhere.
                  </span>
                </li>
              </ul>
            </div>

            <div className='mb-4'>
              <Button
                btnType='primary'
                className='mb-3'
                onClick={() =>
                  window.open(
                    'https://console.cloud.google.com/iam-admin/serviceaccounts/create',
                    '_blank'
                  )
                }
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  stroke-width='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-plus me-1'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M12 5l0 14' />
                  <path d='M5 12l14 0' />
                </svg>
                Create Service Account
              </Button>
            </div>

            <div className='mb-4'>
              <h6 className='fw-semibold mb-3'>Permissions:</h6>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Assign both the{' '}
                    <a
                      href='https://cloud.google.com/iam/docs/roles-permissions#iam.securityReviewer'
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary fw-semibold'
                    >
                      security reviewer{' '}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-external-link '
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                        <path d='M11 13l9 -9' />
                        <path d='M15 4h5v5' />
                      </svg>
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://cloud.google.com/iam/docs/roles-permissions#basic'
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary fw-semibold'
                    >
                      viewer{' '}
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-external-link'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                        <path d='M11 13l9 -9' />
                        <path d='M15 4h5v5' />
                      </svg>
                    </a>{' '}
                    role to this service account. We will need both of those roles assigned to the
                    service account to give us the needed permissions.
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>No other optional permissions are needed.</span>
                </li>
              </ul>
            </div>
          </>
        )

      case 4:
        return (
          <>
            <h3 className='fw-bold fs-1 text-gray-900 mb-2'>Create the access key</h3>

            <p className='text-gray-900 fs-6 mb-4'>
              To create and download the access key please follow these steps in the GCP UI.
            </p>

            <div className='mb-4'>
              <ul className='list-unstyled'>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>Click on the service account you just created</span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Navigate to the <strong>"Keys"</strong> tab
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Click on <strong>"Add key"</strong> and then on{' '}
                    <strong>'Create new Key'</strong>
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Select <strong>"JSON"</strong> for the key type when prompted
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Click on <strong>"Access Control (IAM)"</strong>
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    Click on <strong>"Create"</strong>
                  </span>
                </li>
                <li className='mb-2 d-flex align-items-center'>
                  <span className='me-2'>•</span>
                  <span>
                    This should trigger a download of a JSON file. This contains the security
                    credentials.
                  </span>
                </li>
              </ul>
            </div>

            <div className='mb-4'>
              <p className='text-gray-700 mb-3'>Finally, just upload the file</p>
              <input
                type='file'
                accept='.json'
                onChange={handleFileUpload}
                style={{display: 'none'}}
                id='json-file-upload'
              />
              <Button
                btnType='primary'
                className='mb-3'
                onClick={() => {
                  const fileInput = document.getElementById('json-file-upload')
                  if (fileInput) {
                    fileInput.click()
                  }
                }}
                disabled={uploadedJsonData}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-upload me-2'
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                  <path d='M7 9l5 -5l5 5' />
                  <path d='M12 4l0 12' />
                </svg>
                {uploadedJsonData ? 'JSON file uploaded' : 'Upload JSON file'}
              </Button>
              {uploadedJsonData && (
                <div>
                  <p className='text-success fs-7 mt-2'>✓ JSON file uploaded successfully</p>
                  {isScanning && (
                    <div className='mt-2'>
                      <div className='d-flex align-items-center mb-1'>
                        <div
                          className='spinner-border spinner-border-sm text-info me-2'
                          role='status'
                        >
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        <p className='text-info fs-7 mb-0'>🔍 Running Cloud security scan...</p>
                      </div>
                      <p className='text-muted fs-8'>
                        This may take a few minutes. Please wait while we scan and save the results.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )

      case 5:
        return (
          <>
            <h3 className='fw-bold fs-1 text-gray-900 mb-2'>
              It worked! Everything seems to be connected.
            </h3>

            <p className='text-gray-900 fs-6 mb-4'>
              Your cloud is connected, please answer two questions and you are good to go!
            </p>

            <div className='mb-4'>
              <div className='mb-4'>
                <Input
                  type='text'
                  className='form-control form-control-lg'
                  label='Name your Cloud.'
                  placeholder='GCP cloud infrastructure'
                  value={cloudName}
                  onChange={(e) => setCloudName(e.target.value)}
                  style={{borderRadius: '8px'}}
                />
              </div>

              <div className='mb-4'>
                <label className='form-label fw-semibold'>What is the purpose of your cloud?</label>
                <select
                  className='form-select form-select-lg'
                  value={cloudPurpose}
                  onChange={(e) => setCloudPurpose(e.target.value)}
                  style={{borderRadius: '8px'}}
                >
                  <option value=''>Select...</option>
                  <option value='Development'>Development</option>
                  <option value='Staging'>Staging</option>
                  <option value='Production'>Production</option>
                  <option value='Testing'>Testing</option>
                  <option value='Mixed'>Mixed</option>
                </select>
              </div>
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <>
      <CloudPageTitle
        icon='Cloud.svg'
        title='Cloud Configuration'
        desc={'Google Cloud Platform (GCP) Configuration'}
      >
        Cloud Configuration
      </CloudPageTitle>
      <div className='container-fluid px-0'>
        <div className='row'>
          <div className='col-lg-8'>
            <div className='card border-0 shadow-sm'>
              <div className='card-body p-6'>
                {renderStepContent()}

                <div className='d-flex gap-3 justify-content-end'>
                  {currentStep > 1 && (
                    <Button btnType='default' onClick={handleGoBack}>
                      Go Back
                    </Button>
                  )}
                  <Button
                    btnType='primary'
                    onClick={handleContinue}
                    disabled={
                      isSaving ||
                      isScanning ||
                      (currentStep === 1 && !projectId.trim()) ||
                      (currentStep === 4 && !uploadedJsonData) ||
                      (currentStep === 5 && (!cloudName.trim() || !cloudPurpose))
                    }
                  >
                    {isScanning ? (
                      <>
                        <div
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          style={{width: '16px', height: '16px'}}
                        >
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        Scanning & Saving Results...
                      </>
                    ) : isSaving ? (
                      <>
                        <div
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          style={{width: '16px', height: '16px'}}
                        >
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : currentStep === 5 ? (
                      'Complete Configuration'
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className='col-lg-4 mt-4 mt-lg-0'>
            <div className='card border-0 shadow-sm h-100'>
              <Stepper steps={steps} currentStep={currentStep} totalSteps={steps.length} />
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}

export default CloudConfiguration
